import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { toast } from "sonner";
import { Sparkles, Copy, Loader2, FileText } from "lucide-react";

/**
 * Growth ledger (intelligence programme, Phase D).
 *
 * One click turns the user's own logged quarter into a self-review
 * or promotion case. Evidence is extracted deterministically on the
 * server; only the prose is AI — and it fails soft (you still get the
 * evidence digest + numbers if the model is down).
 */

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

export function GrowthSection() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const ws = useActiveWorkspace();
  const teamId = ws.active?.team.id ?? null;
  const inPersonal = !!ws.active?.team.isPersonal;

  const [mode, setMode] = useState<"self_review" | "promotion">(
    "self_review",
  );
  const [rangeStart, setRangeStart] = useState(isoDaysAgo(90));
  const [rangeEnd, setRangeEnd] = useState(isoDaysAgo(0));

  const review = trpc.growth.review.useMutation({
    onError: e =>
      toast.error(
        (isAr ? "تعذّر الإنشاء: " : "Couldn't generate: ") +
          (e?.message ?? "unknown"),
      ),
  });

  const md = review.data?.markdown ?? "";

  const summaryLine = useMemo(() => {
    const d = review.data?.digest;
    if (!d) return "";
    return isAr
      ? `${d.metrics.weeksActive} أسبوع · ${d.metrics.shippedCount} مُنجَز · ${d.metrics.signalCount} إشارة`
      : `${d.metrics.weeksActive} active weeks · ${d.metrics.shippedCount} shipped · ${d.metrics.signalCount} signals`;
  }, [review.data, isAr]);

  // Work review is meaningless in a personal workspace.
  if (inPersonal) return null;

  return (
    <section className="mb-10">
      <h2 className="text-sm font-medium text-muted-foreground mb-3 inline-flex items-center gap-1.5">
        <Sparkles className="h-4 w-4 text-coral" />
        {isAr ? "مراجعة الأداء والنمو" : "Self-review & growth"}
      </h2>

      <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
        <p className="text-[13px] text-muted-foreground">
          {isAr
            ? "حوّل عملك المُسجَّل خلال فترة إلى مسودة مراجعة ذاتية أو ملف ترقية. الأدلة مستخرجة من إيصالاتك فعلياً."
            : "Turn your logged work over a period into a self-review or promotion-case draft. Evidence is pulled from your real receipts, not invented."}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full border border-border overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setMode("self_review")}
              className={`px-3 py-1.5 ${
                mode === "self_review"
                  ? "bg-coral text-white"
                  : "hover:bg-muted"
              }`}
            >
              {isAr ? "مراجعة ذاتية" : "Self-review"}
            </button>
            <button
              type="button"
              onClick={() => setMode("promotion")}
              className={`px-3 py-1.5 ${
                mode === "promotion"
                  ? "bg-coral text-white"
                  : "hover:bg-muted"
              }`}
            >
              {isAr ? "ملف ترقية" : "Promotion case"}
            </button>
          </div>
          <input
            type="date"
            value={rangeStart}
            onChange={e => setRangeStart(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          />
          <span className="text-xs text-muted-foreground">→</span>
          <input
            type="date"
            value={rangeEnd}
            onChange={e => setRangeEnd(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          />
          <button
            type="button"
            disabled={review.isPending || teamId == null}
            onClick={() =>
              review.mutate({
                mode,
                rangeStart,
                rangeEnd,
                workspaceTeamId: teamId,
                locale: isAr ? "ar" : "en",
              })
            }
            className="inline-flex items-center gap-1.5 rounded-full bg-coral text-white px-4 py-1.5 text-xs font-medium hover:bg-coral/90 disabled:opacity-50"
          >
            {review.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            {isAr ? "إنشاء" : "Generate"}
          </button>
        </div>

        {review.data && (
          <div className="space-y-2 pt-1">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>{summaryLine}</span>
              {review.data.llmFailed && (
                <span className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2 py-0.5">
                  {isAr
                    ? "النص الذكي غير متاح، عُرضت الأدلة فقط"
                    : "AI prose unavailable, evidence only"}
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(md);
                  toast.success(isAr ? "نُسخ" : "Copied");
                }}
                className="ms-auto inline-flex items-center gap-1 hover:text-foreground"
              >
                <Copy className="h-3 w-3" />
                {isAr ? "نسخ Markdown" : "Copy markdown"}
              </button>
            </div>
            <pre className="text-[12px] whitespace-pre-wrap leading-relaxed bg-muted/40 rounded-lg p-3 max-h-[60vh] overflow-y-auto font-sans">
              {md}
            </pre>
          </div>
        )}
      </div>
    </section>
  );
}
